import React from 'react';
import {Checkbox, FormControl, FormControlLabel, FormLabel, Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { extraParameterModelInitial } from '../../../../Models/ExtraParameterModel';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { ExtraParameterFieldType } from '../../../../shared/enum/ExtraParameterFieldType';
import { theme } from '../../../../theme';
import { ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { IShowDialogProps } from '../../components/ClientPages';
import { ClientExtraParameterContext } from '../ClientExtraParameterContext';
import { ExtraParameterFieldTypes, IExtraParameterFieldType } from '../model/ExtraParameterFieldType';

export function ClientExtraParameterModal({showDialog, setShowDialog}: IShowDialogProps) {
    const clientContext = React.useContext(ClientContext); 
    const clientExtraParameterContext = React.useContext(ClientExtraParameterContext); 
    
    const [fieldTypeNotDefinedError, setExtraParameterFieldTypeNotDefinedError] = React.useState(false);
    const [descriptionNotDefinedError, setDescriptionNotDefinedError] = React.useState(false);
    const [answersNotDefinedError, setAnswersNotDefinedError] = React.useState(false);
    
    const hasError = () => {
        const extraParameterFieldTypeNotDefinedError = !clientExtraParameterContext.currentExtraParameter.type;
        const descriptionNotDefinedError = !clientExtraParameterContext.currentExtraParameter.description;
        const answersNotDefinedError = !clientExtraParameterContext.currentExtraParameter.answers &&
            (clientExtraParameterContext.currentExtraParameter.type == ExtraParameterFieldType.SELECT_OPTION || clientExtraParameterContext.currentExtraParameter.type == ExtraParameterFieldType.SELECT_MULTIPLE);

        setExtraParameterFieldTypeNotDefinedError(extraParameterFieldTypeNotDefinedError);
        setDescriptionNotDefinedError(descriptionNotDefinedError);
        setAnswersNotDefinedError(answersNotDefinedError);

        return extraParameterFieldTypeNotDefinedError || descriptionNotDefinedError || answersNotDefinedError;
    };

    const handleModalDiscardButton = () => {
        clearAndCloseExtraParameterModal();
    };

    const clearAndCloseExtraParameterModal = () => {
        setShowDialog(false);
        clientExtraParameterContext.setCurrentExtraParameter(extraParameterModelInitial);
        clientExtraParameterContext.setSelectedExtraParameterFieldType(null);
        clientExtraParameterContext.setActionFieldIndex(-1);
        clientExtraParameterContext.setIsInEditMode(false);
        setExtraParameterFieldTypeNotDefinedError(false);
        setDescriptionNotDefinedError(false);
        setAnswersNotDefinedError(false);
    };

    const handleModalSaveButton = () => {
        if (!hasError()) {
            const extraParameters = [ ...clientContext.extraParameters ];
            if (clientExtraParameterContext.isInEditMode) {
                extraParameters[clientExtraParameterContext.actionFieldIndex] = clientExtraParameterContext.currentExtraParameter;

                if (extraParameters[clientExtraParameterContext.actionFieldIndex].editAction != EditAction.INSERT)
                    extraParameters[clientExtraParameterContext.actionFieldIndex].editAction = EditAction.UPDATE;

                clientContext.setExtraParameters(extraParameters);
                clientExtraParameterContext.setIsInEditMode(false);
            }
            else
                clientContext.setExtraParameters([ ...extraParameters,
                    { ...clientExtraParameterContext.currentExtraParameter,
                        active: true,
                        editAction: EditAction.INSERT } ]);
            
            clearAndCloseExtraParameterModal();
        }
    };
    
    const extraParameterForm = () => {
        return (
            <Grid container spacing={3}>
                {getModalFieldTypes()}
                {(clientExtraParameterContext.isInEditMode && clientExtraParameterContext.currentExtraParameter.editAction != EditAction.INSERT) && getModalStatus()}
                {getModalDescription()}
                {clientExtraParameterContext.selectedExtraParameterFieldType &&
                    (clientExtraParameterContext.selectedExtraParameterFieldType.value == ExtraParameterFieldType.SELECT_OPTION ||
                        clientExtraParameterContext.selectedExtraParameterFieldType.value == ExtraParameterFieldType.SELECT_MULTIPLE) &&
                    getModalAnswerOptions()}
            </Grid>
        );
    };
    
    const getModalFieldTypes = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    fullWidth
                    id="field-type"
                    options={ExtraParameterFieldTypes}
                    value={clientExtraParameterContext.selectedExtraParameterFieldType}
                    onChange={(_, newValue: IExtraParameterFieldType | null) => clientExtraParameterContext.setSelectedExtraParameterFieldType(newValue)}
                    getOptionLabel={(option) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    getOptionSelected={(option, value) => option.name == value.name && option.value == value.value}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Tipo de campo"
                            variant="outlined"
                            error={fieldTypeNotDefinedError && !clientExtraParameterContext.currentExtraParameter.type}
                            helperText={fieldTypeNotDefinedError && !clientExtraParameterContext.currentExtraParameter.type ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };
    
    const getModalStatus = () => {
        return(
            <Grid item xs={6}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Status</FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={clientExtraParameterContext.currentExtraParameter.active}
                                onChange={() => clientExtraParameterContext.setCurrentExtraParameter({ ...clientExtraParameterContext.currentExtraParameter, active: !clientExtraParameterContext.currentExtraParameter.active })}
                                name="Ativo"
                                color="primary" />
                        }
                        label="Ativo" />
                </FormControl>
            </Grid>
        );
    };
    
    const getModalDescription = () => {
        return (
            <Grid item xs={12}>
                <TextField
                    id="description"
                    value={clientExtraParameterContext.currentExtraParameter.description}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => 
                        clientExtraParameterContext.setCurrentExtraParameter({ ...clientExtraParameterContext.currentExtraParameter, description: event.target.value })}
                    fullWidth
                    label="Pergunta"
                    variant="outlined"
                    inputProps={{ maxLength: 300 }}
                    error={descriptionNotDefinedError && !clientExtraParameterContext.currentExtraParameter.description}
                    helperText={descriptionNotDefinedError && !clientExtraParameterContext.currentExtraParameter.description ? 'Campo obrigatório' : ''} />
            </Grid>
        );
    };

    const getModalAnswerOptions = () => {
        return (
            <Grid item xs={12}>
                <TextField
                    id="answer-options"
                    value={clientExtraParameterContext.currentExtraParameter.answers}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => 
                        clientExtraParameterContext.setCurrentExtraParameter({ ...clientExtraParameterContext.currentExtraParameter, answers: event.target.value })}
                    fullWidth
                    label="Opções de resposta"
                    variant="outlined"
                    inputProps={{ maxLength: 700 }}
                    error={answersNotDefinedError && !clientExtraParameterContext.currentExtraParameter.answers}
                    helperText={answersNotDefinedError && !clientExtraParameterContext.currentExtraParameter.answers ? 
                        'Campo obrigatório' : 
                        'Separe as opções de resposta com ;'} />
            </Grid>
        );
    };

    return (
        <BasicConfirmDialog
            title={clientExtraParameterContext.isInEditMode ? 'Editar mensagem chave' : 'Adicionar mensagem chave'}
            description={clientExtraParameterContext.isInEditMode
                ? 'Para editar a mensagem chave, altere os campos que deseja e clique em salvar.'
                : 'Para criar uma nova pergunta, selecione o tipo de campo e preencha os campos de pergunta e opções de resposta.'
            }
            textFirstButton="Descartar"
            handleFirstButton={handleModalDiscardButton}
            textSecondButton={clientExtraParameterContext.isInEditMode ? 'Salvar Alterações' : 'Salvar Mensagem Chave'}
            handleSecondButton={handleModalSaveButton}
            open={showDialog}
            children={extraParameterForm()}
            cssProperties={{ 
                minWidth: '700px',
                maxWidth: '700px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} />
    );

}